import {useCallback} from 'react';

import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {
    getUserTempLimits,
    removeUserTemporaryLimit,
    saveUserTemporaryLimit,
} from 'src/openapi/admin/apis/UserTemporaryLimitsApi';
import {useAppDispatch} from 'src/store/hooks';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import type {SaveOptions} from 'src/components/save-with-reason/save-with-reason-button';
import type {SaveContext} from 'src/hooks/use-common-data-hook';
import type {TempLimitsSettings} from 'src/modules/usage-limits/typedef';
import type {TempLimit} from 'src/openapi/admin/models/TempLimit';
import type {ErrorPayload, SuccessPayload} from 'src/typedef';

type Props = {
    userId: number,
};

const NO_TEMP_LIMITS_ERROR = 'At least on temporary limit must be specified';

export const DEFAULT_TEMP_LIMITS: TempLimitsSettings = {
    expiresOn: '',
    limits: [],
};

export const useUserTemporaryLimits = ({userId}: Props) => {
    const apiDispatch = useAppDispatch();

    const getDispatchAction = useCallback(async (signal: AbortSignal) => {
        const response = await apiDispatch(getUserTempLimits({userId}, {signal}));

        if (response.payload.success) {
            return Promise.resolve({type: '', payload: {
                success: true,
                error: false,
                data: {
                    limits: response.payload.data,
                    expiresOn: response.payload.data.find(Boolean)?.temporaryDate || '',
                },
            } as SuccessPayload<TempLimitsSettings>});
        } else {
            return Promise.resolve({type: '', payload: response.payload});
        }
    }, [userId, apiDispatch]);

    const saveDispatchAction = useCallback(async (
        settings: TempLimitsSettings,
        signal: AbortSignal,
        extra: (SaveOptions & {initialLimits?: TempLimit[]}) | undefined,
        {initialData, setInitialData}: SaveContext<TempLimitsSettings>,
    ) => {
        const {initialLimits, reason} = extra || {};
        const {limits, expiresOn} = settings;

        if (expiresOn && !limits.length) {
            return Promise.resolve({type: '', payload: {
                data: {message: NO_TEMP_LIMITS_ERROR},
                error: true,
                success: false,
            } as ErrorPayload});
        }

        if (!initialLimits) {
            return Promise.resolve({type: '', payload: {
                data: {message: 'No initial limits found'},
                success: false,
                error: true,
            } as ErrorPayload});
        }

        if (expiresOn) {
            const removedLimits = initialData.limits.filter(initialLimit => (
                !limits.find(limit => limit.limitId === initialLimit.limitId)
            ));

            const savedSettings = {
                ...initialData,
                limits: initialData.limits.filter(initial => limits.some(limit => limit.limitId === initial.limitId)),
            };
            if (!isDeepEqual({prev: savedSettings, current: settings})) {
                const saveTemporaryLimitsResponse = await apiDispatch(saveUserTemporaryLimit({
                    userId,
                    expiresOn,
                    userLimitsSaveRequest: {limits, reason},
                }, {signal}));
                if (saveTemporaryLimitsResponse.payload.error) {
                    return Promise.resolve({type: '', payload: saveTemporaryLimitsResponse.payload});
                }
                setInitialData({...settings, limits: [...limits, ...removedLimits]});
            }

            if (removedLimits.length) {
                const removeTemporaryLimitsResponse = await apiDispatch(removeUserTemporaryLimit({
                    userId,
                    userLimitsRemoveRequest: {limitIds: removedLimits.map(({limitId}) => limitId), reason},
                }, {signal}));
                if (removeTemporaryLimitsResponse.payload.error) {
                    return Promise.resolve({type: '', payload: removeTemporaryLimitsResponse.payload});
                }
            }
        } else {
            const removeTemporaryLimitsResponse = await apiDispatch(removeUserTemporaryLimit({
                userId,
                userLimitsRemoveRequest: {limitIds: initialData.limits.map(({limitId}) => limitId), reason},
            }, {signal}));
            if (removeTemporaryLimitsResponse.payload.error) {
                return Promise.resolve({type: '', payload: removeTemporaryLimitsResponse.payload});
            }
        }

        return Promise.resolve({type: '', payload: {
            data: undefined,
            success: true,
            error: false,
        } as SuccessPayload<void>});
    }, [userId, apiDispatch]);

    const hook = useCommonDataHook({
        initValue: DEFAULT_TEMP_LIMITS,
        getDispatchAction,
        saveDispatchAction,
    });
    const {saveChanges: save} = hook;
    const saveChanges = useCallback((initialLimits?: TempLimit[], options?: SaveOptions) => (
        save({initialLimits, ...options})
    ), [save]);

    return {...hook, saveChanges};
};
